/**
 * Sparkles Component
 *
 * Animated sparkle/glitter particles for premium visual effects.
 * Features:
 * - Seeded random positioning (reproducible across renders)
 * - Configurable particle count and sizes
 * - Fade in/out animation with staggered timing
 * - Area-based distribution
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, AbsoluteFill } from 'remotion';
import { seededRandom } from '../utils';

export interface SparklesProps {
  /** Number of sparkle particles */
  count?: number;
  /** Sparkle color (supports rgba) */
  color?: string;
  /** Frame when sparkles start appearing */
  startFrame?: number;
  /** Area definition for particle distribution */
  area?: {
    x: number;       // Left position (0-100%)
    y: number;       // Top position (0-100%)
    width: number;   // Width (0-100%)
    height: number;  // Height (0-100%)
  };
  /** Minimum particle size in pixels */
  minSize?: number;
  /** Maximum particle size in pixels */
  maxSize?: number;
  /** Duration of fade cycle in frames */
  cycleDuration?: number;
  /** Random seed for reproducible positions */
  seed?: number;
  /** Intensity of sparkle glow */
  glowIntensity?: number;
}

interface SparkleParticle {
  x: number;
  y: number;
  size: number;
  phaseOffset: number;
  fadeSpeed: number;
  rotationSpeed: number;
}

export const Sparkles: React.FC<SparklesProps> = ({
  count = 20,
  color = '#ffffff',
  startFrame = 0,
  area = { x: 0, y: 0, width: 100, height: 100 },
  minSize = 2,
  maxSize = 6,
  cycleDuration = 60,
  seed = 12345,
  glowIntensity = 1,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  // Don't render before start frame
  if (relativeFrame < 0) {
    return null;
  }

  // Generate particles with seeded random
  const random = seededRandom(seed);
  const particles: SparkleParticle[] = [];

  for (let i = 0; i < count; i++) {
    particles.push({
      x: area.x + random() * area.width,
      y: area.y + random() * area.height,
      size: minSize + random() * (maxSize - minSize),
      phaseOffset: random() * Math.PI * 2,
      fadeSpeed: 0.8 + random() * 0.4,
      rotationSpeed: (random() - 0.5) * 0.1,
    });
  }

  // Overall entrance fade
  const entranceOpacity = interpolate(
    relativeFrame,
    [0, fps * 0.5],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  return (
    <AbsoluteFill style={{ pointerEvents: 'none', overflow: 'hidden' }}>
      {particles.map((particle, index) => {
        // Calculate sparkle opacity with sine wave
        const phase = (relativeFrame / cycleDuration) * Math.PI * 2 * particle.fadeSpeed + particle.phaseOffset;
        const sparkleOpacity = (Math.sin(phase) + 1) / 2;

        // Subtle rotation
        const rotation = relativeFrame * particle.rotationSpeed;

        // Scale pulsing
        const scale = 0.8 + sparkleOpacity * 0.4;

        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: particle.size,
              height: particle.size,
              opacity: sparkleOpacity * entranceOpacity,
              transform: `translate(-50%, -50%) rotate(${rotation}rad) scale(${scale})`,
            }}
          >
            {/* Four-pointed star shape */}
            <svg
              width={particle.size}
              height={particle.size}
              viewBox="0 0 24 24"
              style={{
                filter: `drop-shadow(0 0 ${particle.size * glowIntensity}px ${color})`,
              }}
            >
              <path
                d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z"
                fill={color}
              />
            </svg>
          </div>
        );
      })}
    </AbsoluteFill>
  );
};

/**
 * SparkleCluster - A concentrated burst of sparkles
 * Useful for highlighting specific elements
 */
export interface SparkleClusterProps {
  /** Center X position (0-100%) */
  centerX?: number;
  /** Center Y position (0-100%) */
  centerY?: number;
  /** Radius of the cluster (0-100%) */
  radius?: number;
  /** Number of sparkles */
  count?: number;
  /** Sparkle color */
  color?: string;
  /** Frame when cluster appears */
  startFrame?: number;
  /** Random seed */
  seed?: number;
}

export const SparkleCluster: React.FC<SparkleClusterProps> = ({
  centerX = 50,
  centerY = 50,
  radius = 15,
  count = 12,
  color = '#FFD700',
  startFrame = 0,
  seed = 54321,
}) => {
  return (
    <Sparkles
      count={count}
      color={color}
      startFrame={startFrame}
      area={{
        x: centerX - radius,
        y: centerY - radius,
        width: radius * 2,
        height: radius * 2,
      }}
      minSize={3}
      maxSize={8}
      cycleDuration={45}
      seed={seed}
      glowIntensity={1.5}
    />
  );
};

/**
 * SparkleTrail - Sparkles that follow a path
 * Useful for drawing attention along a trajectory
 */
export interface SparkleTrailProps {
  /** Starting X position (0-100%) */
  startX?: number;
  /** Starting Y position (0-100%) */
  startY?: number;
  /** Ending X position (0-100%) */
  endX?: number;
  /** Ending Y position (0-100%) */
  endY?: number;
  /** Number of sparkles along the trail */
  count?: number;
  /** Sparkle color */
  color?: string;
  /** Frame when trail starts */
  startFrame?: number;
  /** Trail width */
  width?: number;
}

export const SparkleTrail: React.FC<SparkleTrailProps> = ({
  startX = 20,
  startY = 50,
  endX = 80,
  endY = 50,
  count = 15,
  color = '#ffffff',
  startFrame = 0,
  width = 10,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  if (relativeFrame < 0) {
    return null;
  }

  // Trail progress (0 to 1)
  const progress = interpolate(
    relativeFrame,
    [0, fps * 1],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  const random = seededRandom(99999);

  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      {Array.from({ length: count }).map((_, index) => {
        const t = index / (count - 1);

        // Only show sparkles up to current progress
        if (t > progress) return null;

        const x = startX + (endX - startX) * t + (random() - 0.5) * width;
        const y = startY + (endY - startY) * t + (random() - 0.5) * width;
        const size = 3 + random() * 4;
        const phase = random() * Math.PI * 2;

        const sparkleOpacity = (Math.sin((relativeFrame * 0.1) + phase) + 1) / 2;
        const fadeInOpacity = interpolate(
          relativeFrame - (t * fps * 1),
          [0, fps * 0.3],
          [0, 1],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
        );

        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${x}%`,
              top: `${y}%`,
              width: size,
              height: size,
              opacity: sparkleOpacity * fadeInOpacity,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <svg width={size} height={size} viewBox="0 0 24 24">
              <path
                d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z"
                fill={color}
                style={{ filter: `drop-shadow(0 0 ${size}px ${color})` }}
              />
            </svg>
          </div>
        );
      })}
    </AbsoluteFill>
  );
};
